import React, { createContext, useContext, useState, useEffect, useLayoutEffect } from 'react';
import { apiClient, setAuthToken, clearAuthToken } from '../lib/api';
import { toast } from 'sonner';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  // P0 FIX: Initialize token and sync with API module's memory cache
  const [token, setTokenState] = useState(() => {
    const storedToken = localStorage.getItem('token');
    // Sync with API module's memory cache immediately
    if (storedToken) {
      setAuthToken(storedToken);
    }
    return storedToken;
  });
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [defaultTenantId, setDefaultTenantId] = useState(localStorage.getItem('currentTenantId'));
  const [showReauthBanner, setShowReauthBanner] = useState(false);
  const [reauthReason, setReauthReason] = useState(null);

  // P0 CRITICAL FIX: Use useLayoutEffect to sync token BEFORE any render
  // This ensures the API module has the token before any API calls are made
  useLayoutEffect(() => {
    const storedToken = localStorage.getItem('token');
    if (storedToken && storedToken !== token) {
      setAuthToken(storedToken);
      setTokenState(storedToken);
    }
  }, []);

  // P0 FIX: Listen for reauth-required events from API interceptor
  useEffect(() => {
    const handleReauthRequired = (event) => {
      const { reason, message } = event.detail || {};
      console.warn('[AuthContext] Reauth required:', reason, message);
      setShowReauthBanner(true);
      setReauthReason(reason);
      toast.error(message || 'Session expirée - Reconnectez-vous');
    };
    
    window.addEventListener('ame:reauth-required', handleReauthRequired);
    return () => window.removeEventListener('ame:reauth-required', handleReauthRequired);
  }, []);

  useEffect(() => {
    if (token) {
      // Ensure API module cache is synced
      setAuthToken(token);
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      // P0 HARDENING: Use centralized apiClient (already has auth interceptor)
      const response = await apiClient.get('/auth/me');
      setUser(response.data);
      setShowReauthBanner(false); // Clear banner on successful auth
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  // P0 FIX: Wrapper to sync token with both React state and API module memory cache
  const setToken = (newToken) => {
    setTokenState(newToken);
    setAuthToken(newToken); // Sync with API module's memory cache
  };

  const login = (newToken, userData, tenantId = null) => {
    // P0 FIX: Update both localStorage and memory cache via setAuthToken
    setAuthToken(newToken);
    setTokenState(newToken);
    setUser(userData);
    if (tenantId) {
      localStorage.setItem('currentTenantId', tenantId);
      setDefaultTenantId(tenantId);
    }
  };

  const logout = async () => {
    // P0 iOS Safari DEFINITIVE: Clear both localStorage, memory cache, AND server cookie
    try {
      // Call server to clear HttpOnly cookie (ame_session)
      await apiClient.post('/auth/logout').catch(() => {
        // Silent fail - we still want to clear local state
        console.warn('[AuthContext] Server logout failed, clearing local state anyway');
      });
    } catch (e) {
      // Network error - continue with local logout
    }
    
    clearAuthToken();
    localStorage.removeItem('currentTenantId');
    setTokenState(null);
    setUser(null);
    setDefaultTenantId(null);
  };

  const updateUser = async (updates) => {
    try {
      // P0 HARDENING: Use centralized apiClient
      const response = await apiClient.put('/auth/me', updates);
      setUser(response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to update user:', error);
      throw error;
    }
  };
  
  // P0 FIX: Reset session handler
  const resetSession = () => {
    console.log('[AuthContext] Resetting session');
    logout();
    setShowReauthBanner(false);
    setReauthReason(null);
    // Redirect to login
    window.location.href = '/auth';
  };

  return (
    <AuthContext.Provider value={{ 
      token, 
      user, 
      loading, 
      login, 
      logout, 
      updateUser, 
      defaultTenantId,
      // P0 FIX: Expose reauth state
      showReauthBanner,
      reauthReason,
      resetSession
    }}>
      {/* P0 FIX: Reauth banner - Reduced z-index to not block drawers */}
      {showReauthBanner && (
        <div className="fixed top-0 left-0 right-0 bg-yellow-500 text-black p-2 z-30 flex items-center justify-center gap-4 shadow-lg pointer-events-auto">
          <span className="font-medium text-sm">⚠️ Session expirée ({reauthReason})</span>
          <button 
            onClick={resetSession}
            className="px-3 py-1 bg-black text-white rounded text-sm hover:bg-gray-800 font-medium"
          >
            Reconnecter
          </button>
        </div>
      )}
      {/* Add top padding when banner is shown */}
      <div className={showReauthBanner ? 'pt-12' : ''}>
        {children}
      </div>
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);